import React from 'react';
import { Box, Flex } from '@chakra-ui/react';
import {
  HeadingText,
  BodyText,
  Tag,
  DisplayText,
} from '@spotoninc/nexus-react';
import {
  ArrowUpIcon,
  ArrowDownIcon,
  SalesIcon,
} from '@spotoninc/nexus-web-assets';

/**
 * Reskinned Benchmark Overview Stat component that matches the Figma design
 * This component is used when the showReskin feature flag is enabled
 */
const ReskinnedBenchmarkOWStat = props => {
  const {
    header,
    num,
    denom,
    numDesc,
    denomDesc,
    avgCost,
    avgInc,
    bicCost,
    bicInc,
    comparisonValue,
    isOutperforming = 'positive',
  } = props;

  // Determine if this is a cost or income card based on the header
  const isCostCard = header.toLowerCase().includes('cost');

  // Set colors based on card type
  const cardBgColor = '#F4F7FA'; // From Figma design
  const textColor = '#0A091B'; // From Figma design
  const labelColor = '#37456C'; // From Figma design

  // Set tag colors based on performance
  const iconColor =
    isOutperforming === 'positive'
      ? '#2AA157'
      : isOutperforming === 'negative'
        ? '#ED5E5A'
        : '#6E7A98'; // Grey color for neutral state

  // Performance text with bold words
  const getPerformanceText = () => {
    if (isOutperforming === 'positive') {
      return (
        <>
          <span style={{ fontWeight: 700 }}>Outperforming</span> the avg.
          industry benchmark by
        </>
      );
    } else if (isOutperforming === 'negative') {
      return (
        <>
          <span style={{ fontWeight: 700 }}>Underperforming</span> the avg.
          industry benchmark by
        </>
      );
    }
    return null; // No text for neutral state
  };

  // Get the appropriate values based on card type
  const avgValue = isCostCard ? avgCost : avgInc;
  const bicValue = isCostCard ? bicCost : bicInc;

  return (
    <Box borderRadius="12px" bg={cardBgColor} p="24px" width="100%">
      <Flex direction="column" gap="24px">
        {/* Top section with title and performance tag */}
        <Flex alignItems="flex-end" width="100%">
          <Flex direction="column" gap="4px">
            <Flex alignItems="center">
              <HeadingText
                size="medium"
                style={{ fontSize: 'var(--font-size-200, 1.125rem)' }}
              >
                {header}
              </HeadingText>
            </Flex>
            <Flex alignItems="center" gap="8px">
              {isOutperforming !== 'neutral' && (
                <BodyText
                  size="medium"
                  color={labelColor}
                  style={{
                    fontSize: 'var(--font-size-75, 0.875rem)',
                  }}
                >
                  {getPerformanceText()}
                </BodyText>
              )}
              {comparisonValue && comparisonValue !== '-' && (
                <Tag
                  variant={
                    isOutperforming === 'positive'
                      ? 'success'
                      : isOutperforming === 'negative'
                        ? 'danger'
                        : 'neutral'
                  }
                  emphasis="subtle"
                  icon={
                    isOutperforming === 'positive'
                      ? ArrowUpIcon
                      : isOutperforming === 'negative'
                        ? ArrowDownIcon
                        : null
                  }
                >
                  {comparisonValue}
                </Tag>
              )}
            </Flex>
          </Flex>
        </Flex>

        {/* Bottom section with values */}
        <Flex width="100%">
          <Flex
            justifyContent="space-between"
            alignItems="flex-end"
            width="100%"
            gap={{ base: '16px', md: '24px', lg: '32px' }}
          >
            {/* Your Cost/Income */}
            <Flex direction="column" gap="2px" minWidth="110px" flex="1">
              <BodyText
                size="medium"
                color={labelColor}
                style={{ fontSize: 'var(--font-size-75, 0.875rem)' }}
              >
                {isCostCard ? 'Your Cost' : 'Your Income'}
              </BodyText>
              <DisplayText
                size="large"
                color="var(--semantic-color-text-neutral-strongest-none)"
                style={{
                  fontFamily: 'var(--font-family-sans, Poppins)',
                  fontSize: 'var(--font-size-700, 2.5rem)',
                  fontStyle: 'normal',
                  fontWeight: 600,
                  lineHeight: 'var(--font-line-height-700, 3rem)',
                  fontFeatureSettings: "'liga' off",
                }}
              >
                {num || '-'}
              </DisplayText>
            </Flex>

            {/* Avg Cost/Income */}
            <Flex direction="column" gap="2px" minWidth="110px" flex="1">
              <BodyText
                size="medium"
                color={labelColor}
                style={{ fontSize: 'var(--font-size-75, 0.875rem)' }}
              >
                {isCostCard ? 'Avg Cost' : 'Avg Income'}
              </BodyText>
              <Flex alignItems="center" gap="4px">
                <SalesIcon
                  color={iconColor}
                  style={{
                    width: '1.125rem',
                    height: '1.125rem',
                  }}
                />
                <HeadingText
                  size="small"
                  color={textColor}
                  style={{
                    fontSize: 'var(--font-size-200, 1.125rem)',
                  }}
                >
                  {avgValue || '-'}
                </HeadingText>
              </Flex>
            </Flex>

            {/* Best in Class */}
            <Flex direction="column" gap="2px" minWidth="110px" flex="1">
              <BodyText
                size="medium"
                color={labelColor}
                style={{ fontSize: 'var(--font-size-75, 0.875rem)' }}
              >
                Best in Class
              </BodyText>
              <Flex alignItems="center" gap="4px">
                <SalesIcon
                  color={iconColor}
                  style={{
                    width: '1.125rem',
                    height: '1.125rem',
                  }}
                />
                <HeadingText
                  size="small"
                  color={textColor}
                  style={{
                    fontSize: 'var(--font-size-200, 1.125rem)',
                  }}
                >
                  {bicValue || '-'}
                </HeadingText>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </Box>
  );
};

export default ReskinnedBenchmarkOWStat;
